import React, { useMemo } from "react";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

const styles = {
  document: {
    color: "#000000",
    fontFamily: "sans-serif",
    fontSize: "9pt",
  },
  header: {
    marginBottom: "12pt",
    textAlign: "center",
  },
  title: {
    fontSize: "18pt",
    margin: "0 0 6pt",
  },
  headerLine: {
    margin: "3pt 0",
  },
  sectionTable: {
    borderCollapse: "collapse",
    marginBottom: "8pt",
    width: "100%",
  },
  sectionHeader: {
    backgroundColor: "#afaeae",
    border: "1px solid #D3D3D3",
    padding: "4pt",
    textAlign: "center",
  },
  movementTable: {
    borderCollapse: "collapse",
    fontSize: "8pt",
    marginBottom: "10pt",
    width: "100%",
  },
  highlightedCell: {
    backgroundColor: "#f4c181",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    verticalAlign: "top",
  },
  cell: {
    backgroundColor: "#f0f0f0",
    border: "1px solid #D3D3D3",
    padding: "3pt",
    verticalAlign: "top",
  },
  alert: {
    color: "#ff0000",
  },
};

const MouvementsLocationPDF = ({ lignesMouvements, dateDu, dateAu, mouvement }) => {
  // 1 Mémorisation des données filtrées
  const { lignesEntrees, lignesSorties } = useMemo(() => {
    if (!lignesMouvements?.length) return { lignesEntrees: [], lignesSorties: [] };

    const dateDebutRange = dayjs(dateDu, "YYYY-MM-DD");
    const dateFinRange = dayjs(dateAu, "YYYY-MM-DD");

    let result;
    switch (mouvement) {
      case 1:
        result = { lignesEntrees: lignesMouvements, lignesSorties: [] };
        break;
      case 2:
        result = { lignesEntrees: [], lignesSorties: lignesMouvements };
        break;
      case 3:
        result = {
          lignesEntrees: lignesMouvements.filter((item) =>
            dayjs(item.location_date_debut, "YYYY-MM-DD").isBetween(dateDebutRange, dateFinRange, null, '[]')
          ),
          lignesSorties: lignesMouvements.filter((item) =>
            dayjs(item.location_date_fin, "YYYY-MM-DD").isBetween(dateDebutRange, dateFinRange, null, '[]')
          )
        };
        break;
      default:
        result = { lignesEntrees: [], lignesSorties: [] };
    }
    return result;
  }, [lignesMouvements, dateDu, dateAu, mouvement]);

  // Fonction helper pour extraire les informations de contact
  const getContactInfo = (ligne) => {
    const source = ligne.location_requete
      ? ligne.location_requete.requete_locataire.locataire_personne
      : ligne.location_lot.lot_proprietaire.proprietaire_personne;

    return {
      portable1: source.personne_portable1 || "",
      portable2: source.personne_portable2 || "",
      telephone1: source.personne_tel1 || "",
    };
  };

  // Fonction helper pour formater le solde
  const formatSolde = (solde) => {
    const amount = parseInt(solde);
    return amount !== 0 ? parseFloat(solde).toLocaleString("fr-FR", {
      style: "currency",
      currency: "EUR",
    }) : null;
  };

  // Composant pour une ligne de mouvement
  const LigneMouvement = ({ ligne, index }) => {
    const { portable1, portable2, telephone1 } = getContactInfo(ligne);
    const contactsText = `${portable1}${portable2}${telephone1}`;
    const soldeFormate = formatSolde(ligne.solde);

    const nomPersonne = ligne.location_requete
      ? ligne.location_requete.requete_locataire.locataire_personne.personne_nom
      : "PROPRIETAIRE";

    const adressePersonne = ligne.location_requete
      ? ligne.location_requete.requete_locataire.locataire_personne.personne_adresse.adresse_gmaps_adresse
      : ligne.location_lot.lot_proprietaire.proprietaire_personne.personne_adresse.adresse_gmaps_adresse;

    return (
      <table key={index} style={styles.movementTable}>
        <tbody>
          <tr>
            <td width="33%" style={styles.highlightedCell}>
              {nomPersonne}
            </td>
            <td width="34%" style={styles.highlightedCell}>
              {ligne.location_lot.lot_reference} - {ligne.location_lot.lot_designation}
            </td>
            <td width="33%" style={styles.highlightedCell}>
              Arrivée : le {dayjs(ligne.location_date_debut, "YYYY-MM-DD").format("DD/MM/YYYY")}
              à {ligne.location_heure_arrivee}
            </td>
          </tr>
          <tr>
            <td width="33%" style={styles.cell}>
              {contactsText}
            </td>
            <td width="34%" style={styles.cell}>
              {ligne.location_lot.lot_proprietaire.proprietaire_personne.personne_nom}
            </td>
            <td width="33%" style={styles.cell}>
              Départ : le {dayjs(ligne.location_date_fin, "YYYY-MM-DD").format("DD/MM/YYYY")}
              à {ligne.location_heure_depart}
            </td>
          </tr>
          <tr>
            <td width="33%" style={styles.cell}>
              {adressePersonne}
            </td>
            <td width="34%" style={styles.cell}>
              {ligne.location_lot.lot_adresse.adresse_gmaps_adresse}
            </td>
            <td width="33%" style={styles.cell}></td>
          </tr>
          <tr>
            <td width="33%" style={styles.cell}>
              {soldeFormate}
            </td>
            <td width="34%" style={styles.cell}></td>
            <td width="33%" style={styles.cell}></td>
          </tr>
          <tr>
            <td width="33%" style={styles.cell}>
              {ligne.location_nb_adultes} adultes {ligne.location_nb_enfants} enfants{" "}
              {ligne.location_nb_bebes} bébés
            </td>
            <td width="34%" style={styles.cell}></td>
            <td width="33%" style={styles.cell}>
              <span style={styles.alert}>{ligne.croisement}</span>
            </td>
          </tr>
          <tr>
            <td width="33%" style={{ ...styles.cell, height: "15mm" }}>
              Desiderata : {ligne.location_desiderata}
            </td>
            <td width="34%" style={{ ...styles.cell, height: "15mm" }}></td>
            <td width="33%" style={{ ...styles.cell, height: "15mm" }}>
              Remarques : {ligne.location_remarques}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  // Composant pour l'en-tête de section
  const SectionHeader = ({ title }) => (
    <table style={styles.sectionTable}>
      <thead>
        <tr>
          <th width="100%" style={styles.sectionHeader}>
            <strong>{title}</strong>
          </th>
        </tr>
      </thead>
    </table>
  );

  // Fonction pour rendre une section (ENTREES ou SORTIES)
  const renderSection = (lignes, title) => {
    if (lignes.length === 0) return null;

    return (
      <>
        <SectionHeader title={title} />
        {lignes.map((ligne, index) => (
          <LigneMouvement key={`${title}-${index}`} ligne={ligne} index={index} />
        ))}
      </>
    );
  };

  const periodeText = `Période : ${dateDu.format("DD/MM/YYYY")} au ${dateAu.format("DD/MM/YYYY")}`;

  return (
    <div style={styles.document}>
      <div style={styles.header}>
        <h1 style={styles.title}>LISTE ENTREES/SORTIES</h1>
        <p style={styles.headerLine}>
          <span>
            <strong>{periodeText}</strong>
          </span>
        </p>
      </div>
      {renderSection(lignesEntrees, "ENTREES")}
      {renderSection(lignesSorties, "SORTIES")}
    </div>
  );
};

export default React.memo(MouvementsLocationPDF);
